import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import { getValueColor, sortBands } from "../utils/uiUtils";
import { EegDataRecord, EEGMetric, EEGRecording } from "../types";
import { calculateGlobalMeanZscore, calculateImprovement, groupByFrequencyBand } from "../utils/calculationsUtils";
import { ComparisonMetricDisplay } from "./ComparisonMetricDisplay";
import { useEegDataForMetric, useAllEegData } from "../utils/useEeegData";

type BandStateProps = {
    metric: EEGMetric;
    showDataInTable: (band: string, records: Array<EegDataRecord>) => void;
    firstEEG: EEGRecording;
    secondEEG?: EEGRecording;
    allEegData: ReturnType<typeof useAllEegData>;
};

export const BandState = ({ metric, showDataInTable, secondEEG, allEegData }: BandStateProps) => {
    const { firstEEGData, secondEEGData, firstEEGDataStatus, isFetchingSecondEEGData } = useEegDataForMetric(
        metric.id,
        allEegData
    );

    const separateByBand = groupByFrequencyBand(firstEEGData);
    const separateComparisonByBand = groupByFrequencyBand(secondEEGData);

    if (firstEEGDataStatus === "loading") return <CircularProgress />;
    if (firstEEGDataStatus === "error") return <Typography>Error loading metric</Typography>;

    return (
        <Grid container spacing={2}>
            {sortBands(separateByBand).map(([band, records]) => {
                const mean = calculateGlobalMeanZscore(records);
                const comparisonRecords = separateComparisonByBand[band] || [];

                const comparisonMean = comparisonRecords.length > 0 ? calculateGlobalMeanZscore(comparisonRecords) : 0;
                const improvement = comparisonMean !== 0 ? calculateImprovement(mean, comparisonMean) : 0;

                return (
                    <Grid item xs={6} sm={4} md={1.5} key={band}>
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                backgroundColor: "#fafafa",
                                borderLeft: `4px solid ${getValueColor(mean as number)}`,
                                textAlign: "center",
                            }}
                        >
                            <Box display="flex" justifyContent="center" alignItems="center">
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                    {band}
                                </Typography>
                            </Box>
                            <ComparisonMetricDisplay
                                firstEEGMetricMean={mean}
                                secondEEGMetricMean={comparisonMean}
                                improvement={improvement}
                                onFirstEEGClick={() => showDataInTable(band, records)}
                                onSecondEEGClick={() => showDataInTable(band, comparisonRecords)}
                                variant="h6"
                                showComparison={!!secondEEG?.id}
                                secondEEGIsFetching={isFetchingSecondEEGData}
                            />
                        </Box>
                    </Grid>
                );
            })}
        </Grid>
    );
};
